import { CommonModule, NgFor } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import {
  Client,
  FirmwareUpdateGroupStatus,
} from '../../../../../../../api/api-reference';
import { MultiSelectDropdownSearchComponent } from '../../../../multi-select-dropdown-search/multi-select-dropdown-search.component';
import { FilterPanelComponent } from '../../filter-panel.component';

export interface FirmwareUpdateGroupFilterOutput {
  meterMakeIds?: string[];
  meterModelIds?: string[];
  statuses?: FirmwareUpdateGroupStatus[];
}

@Component({
  selector: 'app-firmware-update-group-filter',
  standalone: true,
  imports: [CommonModule, NgFor, TranslateModule, FilterPanelComponent, MultiSelectDropdownSearchComponent],
  templateUrl: './firmware-update-group-filter.component.html',
  styleUrl: './firmware-update-group-filter.component.scss',
})
export class FirmwareUpdateGroupFilterComponent implements OnInit {
  @Input() numberOfResults: number = 0;
  @Input() selectedMeterMakeIds: string[] = [];
  @Input() selectedMeterModelIds: string[] = [];
  @Input() selectedStatuses?: FirmwareUpdateGroupStatus[];

  @Output() applied = new EventEmitter<FirmwareUpdateGroupFilterOutput>();

  readonly statuses = Object.values(FirmwareUpdateGroupStatus);
  private readonly statusDisplayTranslationKeys: Record<FirmwareUpdateGroupStatus, string> = {
    [FirmwareUpdateGroupStatus.Scheduled]: 'PENDING',
    [FirmwareUpdateGroupStatus.InProgress]: 'INPROGRESS',
    [FirmwareUpdateGroupStatus.Completed]: 'SUCCESSFUL',
    [FirmwareUpdateGroupStatus.Failed]: 'FAILED',
    [FirmwareUpdateGroupStatus.Deactivated]: 'DEACTIVATED',
    [FirmwareUpdateGroupStatus.Aborted]: 'ABORTED',
  };

  draftMeterMakeIds: string[] = [];
  draftMeterModelIds: string[] = [];
  draftStatuses: FirmwareUpdateGroupStatus[] = [];
  fetchMeterMakes = () => this.client.getAllMeterMakersForSetupOptions();
  fetchMeterModels = () => this.client.getAllMeterModelForMeterMakes(this.draftMeterMakeIds);

  get activeFilterCount(): number {
    return (
      this.selectedMeterMakeIds.length +
      this.selectedMeterModelIds.length +
      (this.selectedStatuses?.length ?? 0)
    );
  }

  constructor(private client: Client) { }

  ngOnInit(): void {
    this.reset();
  }

  onMeterMakeListReceived(list: string[]): void {
    this.draftMeterMakeIds = list;
    this.draftMeterModelIds = [];
    this.fetchMeterModels = () => this.client.getAllMeterModelForMeterMakes(this.draftMeterMakeIds);
  }

  onMeterModelListReceived(list: string[]): void {
    this.draftMeterModelIds = list;
  }

  toggleStatus(status: FirmwareUpdateGroupStatus): void {
    this.draftStatuses = this.draftStatuses.includes(status)
      ? this.draftStatuses.filter((item) => item !== status)
      : [...this.draftStatuses, status];
  }

  statusTranslationKey(status: FirmwareUpdateGroupStatus): string {
    return this.statusDisplayTranslationKeys[status] ?? status.toUpperCase();
  }

  onApply(): void {
    this.applied.emit({
      meterMakeIds: this.draftMeterMakeIds,
      meterModelIds: this.draftMeterModelIds,
      statuses: this.draftStatuses.length ? this.draftStatuses : undefined,
    });
  }

  onCancel(): void {
    this.reset();
  }

  private reset(): void {
    this.draftMeterMakeIds = [...this.selectedMeterMakeIds];
    this.draftMeterModelIds = [...this.selectedMeterModelIds];
    this.draftStatuses = [...(this.selectedStatuses ?? [])];
    this.fetchMeterModels = () => this.client.getAllMeterModelForMeterMakes(this.draftMeterMakeIds);
  }
}
